import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

/**
 * ThemeContext — tema Claro/Escuro/Sistema do painel admin.
 *
 * Como funciona: o app foi escrito dark-first (classes slate-900/950 etc.).
 * Em vez de espalhar `dark:` em milhares de classes, o tema claro sobrescreve
 * as CSS variables de cor do Tailwind v4 dentro do escopo `.light` (ver
 * index.css). O resolvedTheme vira classe no wrapper do Layout/Login — a
 * página pública /agendar e o kiosk ficam FORA do escopo de propósito (têm
 * tema próprio, sempre claro).
 *
 * Persistência: localStorage `farisx_theme` ('dark' | 'light' | 'system').
 * 'system' acompanha prefers-color-scheme em tempo real.
 */

export type ThemePref = 'dark' | 'light' | 'system';
type Resolved = 'dark' | 'light';

const STORAGE_KEY = 'farisx_theme';

function readPref(): ThemePref {
  const v = localStorage.getItem(STORAGE_KEY);
  return v === 'light' || v === 'system' ? v : 'dark';
}

function systemPrefers(): Resolved {
  return window.matchMedia('(prefers-color-scheme: light)').matches ? 'light' : 'dark';
}

interface ThemeContextValue {
  theme: ThemePref;
  resolved: Resolved;
  setTheme: (t: ThemePref) => void;
}

const ThemeContext = createContext<ThemeContextValue>({
  theme: 'dark',
  resolved: 'dark',
  setTheme: () => undefined,
});

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [theme, setThemeState] = useState<ThemePref>(() => readPref());
  const [systemResolved, setSystemResolved] = useState<Resolved>(() => systemPrefers());

  // Acompanha mudança do SO quando em modo 'system'
  useEffect(() => {
    const mq = window.matchMedia('(prefers-color-scheme: light)');
    const onChange = () => setSystemResolved(mq.matches ? 'light' : 'dark');
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  const setTheme = useCallback((t: ThemePref) => {
    setThemeState(t);
    try {
      localStorage.setItem(STORAGE_KEY, t);
    } catch {
      /* quota — ignora */
    }
  }, []);

  const resolved: Resolved = theme === 'system' ? systemResolved : theme;

  const value = useMemo(() => ({ theme, resolved, setTheme }), [theme, resolved, setTheme]);

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextValue {
  return useContext(ThemeContext);
}
